import { useState, useEffect } from 'react';
import type { ChangeEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api, { moduleConfigAPI } from '../services/api';
import type { ModuleConfig } from '../types';
import { MODULE_CATEGORIES } from './ModuleConfigPage';
import { Switch } from '../components/ui/Switch';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import toast from '../lib/toast';
import {
  Building2, Loader2, AlertCircle, Lock, Upload, ImageIcon, Save, Sparkles,
} from 'lucide-react';
import { RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// ============================================================================
// P1-B (2026-09-08, spec ADDENDUM "Condo Admin Panel"): /admin/condo
// - Condo identity: name, address (city + addressData.street/number), description.
// - Module on/off toggles reusing GET/POST /condos/:id/modules (ModuleConfigPage
//   contract; backend re-validates every toggle — never trust the client).
// - Plan-bounded: effective modules come from GET /entitlements/:condoId.
//   A module whose plan feature is not in the condo's effective feature list
//   renders LOCKED with an upgrade CTA to /pricing. No new backend endpoints.
// - Logo upload (2026-09-08): LIVE POST /api/condos/:id/logo — JSON body with a
//   base64/data-url image (client caps at 2MB, images only); backend writes
//   /var/www/vecindus-uploads/condos/ and returns { success, data: { logoUrl } }.
// ============================================================================

// Condo module-config id -> canonical plan feature id (billing/plan-source MODULE_SCOPES).
// Modules without a mapping are treated as always-available (core).
const MODULE_PLAN_FEATURE: Record<string, string> = {
  finanzas: 'financial-reports',
  ingresos: 'financial-reports',
  gastos: 'expenses',
  cotizaciones: 'reports-advanced',
  morosos: 'morosos',
  amenidades: 'amenities',
  calendario: 'calendar',
  quejas: 'complaints',
  anuncios: 'announcements',
  votaciones: 'voting',
  qr_acceso: 'qr-access',
  panic_button: 'panic',
  visitantes: 'security',
  home_tareas: 'home-tasks',
  home_compras: 'home-shopping',
  home_mascotas: 'home-pets',
  home_vehiculos: 'home-vehicles',
  home_presupuesto: 'home-budget',
};

const CondoAdminPanel = () => {
  const { user } = useAuth();
  const condoId = user?.condoId;
  const { t } = useTranslation('common');

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // Identity form
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [street, setStreet] = useState('');
  const [number, setNumber] = useState('');
  const [description, setDescription] = useState('');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  // (2026-09-08) Logo upload: POST /condos/:id/logo with JSON body. Dual key on
  // purpose: the live backend (src/modules/condo/routes.js POST /:id/logo) reads
  // req.body.file, while the 2026-09-08 task brief documents { data: ... }; the
  // backend ignores unknown keys, so sending both satisfies either contract.
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoError, setLogoError] = useState('');
  const [savingIdentity, setSavingIdentity] = useState(false);
  const [identityMsg, setIdentityMsg] = useState('');

  // Modules
  const [moduleConfig, setModuleConfig] = useState<Record<string, boolean>>({});
  const [planFeatures, setPlanFeatures] = useState<Set<string>>(new Set());
  const [planName, setPlanName] = useState<string>('');
  const [entitlementsAvailable, setEntitlementsAvailable] = useState(true);
  const [savingModules, setSavingModules] = useState(false);
  // Module provenance from GET /condos/:id/modules (per-row source) and the
  // id of the module currently going through the reset-to-default flow.
  const [moduleSources, setModuleSources] = useState<Record<string, string>>({});
  const [resettingModule, setResettingModule] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!condoId) { setLoading(false); return; }
      try {
        const [condoRes, modulesRes, entRes] = await Promise.all([
          api.get(`/condos/${condoId}`),
          moduleConfigAPI.getAll(condoId),
          api.get(`/entitlements/${condoId}`).catch(() => null),
        ]);
        const condo = condoRes.data?.data?.condominium ?? condoRes.data?.condominium ?? condoRes.data?.data ?? {};
        setName(condo.name || '');
        setCity(condo.city || '');
        const addr = condo.address_data || {};
        setStreet(addr.street || '');
        setNumber(addr.number || '');
        setDescription(condo.description || '');
        setLogoUrl(condo.logoUrl || condo.logo_url || null);

        applyModulesPayload(modulesRes);

        const ent = entRes?.data?.data ?? entRes?.data;
        if (ent && Array.isArray(ent.modules)) {
          setPlanFeatures(new Set(ent.modules as string[]));
          setPlanName(String(ent.plan || ''));
          setEntitlementsAvailable(true);
        } else {
          // Fail-open: if entitlements are unavailable we do not lock toggles,
          // but the state is surfaced. Backend still re-validates on save.
          setEntitlementsAvailable(false);
        }
      } catch (err: any) {
        setLoadError(err?.response?.data?.error || 'No se pudo cargar el panel del condominio.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [condoId]);

  const isModuleLocked = (moduleId: string): boolean => {
    if (!entitlementsAvailable) return false; // fail-open, surfaced in UI
    const feature = MODULE_PLAN_FEATURE[moduleId];
    if (!feature) return false; // core module, no plan mapping
    return !planFeatures.has(feature);
  };

  const handleToggle = (moduleId: string) => {
    if (isModuleLocked(moduleId)) {
      toast.error('Este módulo no está incluido en tu plan actual.');
      return;
    }
    setModuleConfig(prev => ({ ...prev, [moduleId]: !prev[moduleId] }));
  };

  // (2026-10-01, pre-launch) Reset-to-default support. Parses the GET
  // /condos/:id/modules payload once, keeping per-row source provenance
  // (override | legacy_config | global_default) for the reset affordance.
  const applyModulesPayload = (res: any) => {
    const mods = Array.isArray(res?.data) ? res.data : (res?.data?.data ?? []);
    const config: Record<string, boolean> = {};
    const sources: Record<string, string> = {};
    mods.forEach((mod: ModuleConfig) => {
      config[mod.module_id] = mod.is_active;
      if (typeof mod.source === 'string') sources[mod.module_id] = mod.source;
    });
    Object.values(MODULE_CATEGORIES).forEach(category => {
      category.modules.forEach(mod => {
        if (config[mod.id] === undefined) config[mod.id] = true;
      });
    });
    setModuleConfig(config);
    setModuleSources(sources);
  };

  const refetchModules = async () => {
    if (!condoId) return;
    const res = await moduleConfigAPI.getAll(condoId);
    applyModulesPayload(res);
  };

  // Reset to default (2026-10-01, pre-launch): clears the condo-level choice
  // for one module so it follows the plan/global default again. Override rows
  // map to the plan feature id (MODULE_PLAN_FEATURE) and use the super_admin
  // admin-features DELETE; legacy_config rows use the condo-admin modules
  // DELETE. On success we re-fetch so the row shows global_default again.
  const handleResetModule = async (moduleId: string) => {
    if (!condoId) return;
    if (!window.confirm(t('condoAdmin.resetConfirm'))) return;
    setResettingModule(moduleId);
    try {
      const source = moduleSources[moduleId];
      if (source === 'override') {
        const featureId = MODULE_PLAN_FEATURE[moduleId];
        if (!featureId) {
          // No reliable module->featureId mapping: do not guess (task bounds).
          setResettingModule(null);
          toast.error(t('condoAdmin.resetMappingError'));
          return;
        }
        await moduleConfigAPI.resetFeature(condoId, featureId);
      } else {
        await moduleConfigAPI.resetModule(condoId, moduleId);
      }
      toast.success(t('condoAdmin.resetSuccess'));
      await refetchModules();
    } catch (err: any) {
      console.error('Reset to default failed:', err);
      toast.error(err?.response?.data?.error || t('condoAdmin.resetError'));
    } finally {
      setResettingModule(null);
    }
  };

  // (2026-09-08) Logo uploader: client-side 2MB + image-type validation, then
  // POST /api/condos/:id/logo (backend: condo/routes.js, admin/super_admin).
  // Response contract: { success, data: { logoUrl } }.
  const LOGO_MAX_BYTES = 2 * 1024 * 1024;
  const LOGO_ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  const handleLogoFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    setLogoError('');
    if (!file) return;
    if (!LOGO_ALLOWED_TYPES.includes(file.type)) {
      setLogoError('Formato no soportado. Usa JPEG, PNG, GIF o WebP.');
      return;
    }
    if (file.size > LOGO_MAX_BYTES) {
      setLogoError('La imagen supera el límite de 2MB.');
      return;
    }
    if (!condoId) {
      setLogoError('No se pudo determinar el condominio.');
      return;
    }
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(new Error('No se pudo leer el archivo'));
      reader.readAsDataURL(file);
    }).catch(() => { setLogoError('No se pudo leer el archivo.'); return null; });
    if (!dataUrl) return;
    setLogoBusy(true);
    try {
      const res = await api.post(`/condos/${condoId}/logo`, { file: dataUrl, data: dataUrl });
      const url = res.data?.data?.logoUrl || res.data?.logoUrl || null;
      if (url) setLogoUrl(url);
      toast.success('Logo actualizado');
    } catch (err: any) {
      const status = err?.response?.status;
      const backendMsg = err?.response?.data?.error || err?.response?.data?.message;
      let msg = backendMsg || 'No se pudo subir el logo.';
      if (status === 413) msg = 'La imagen supera el límite de 2MB.';
      else if (status === 415) msg = 'Formato no soportado. Usa JPEG, PNG, GIF o WebP.';
      setLogoError(msg);
    } finally {
      setLogoBusy(false);
    }
  };

  const handleSaveIdentity = async () => {
    if (!condoId) return;
    setSavingIdentity(true);
    setIdentityMsg('');
    try {
      // PATCH /condos/:id — allowedUpdates whitelists name, city, addressData.
      // NOTE: `description` is NOT yet whitelisted by the backend; it is sent
      // anyway so it persists as soon as backend adds it (flagged in report).
      await api.patch(`/condos/${condoId}`, {
        name,
        city,
        description,
        addressData: { street, number },
      });
      toast.success('Identidad del condominio guardada');
      setIdentityMsg('Guardado');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Error al guardar la identidad');
      setIdentityMsg('');
    } finally {
      setSavingIdentity(false);
    }
  };

  const handleSaveModules = async () => {
    if (!condoId) return;
    setSavingModules(true);
    try {
      const payload = Object.entries(moduleConfig).map(([moduleId, is_active]) => ({
        module_id: moduleId,
        is_active,
      }));
      await moduleConfigAPI.update(condoId, payload);
      localStorage.setItem('moduleConfig', JSON.stringify(moduleConfig));
      toast.success('Configuración de módulos guardada');
    } catch (err: any) {
      console.error('Module save failed:', err);
      toast.error(err?.response?.data?.error || 'Error al guardar módulos (el servidor re-valida contra el plan).');
    } finally {
      setSavingModules(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-full bg-[#1a1a1a]">
        <Loader2 className="w-8 h-8 text-[#36CCD8] animate-spin" />
      </div>
    );
  }

  if (!condoId) {
    return (
      <div className="flex flex-col items-center justify-center min-h-full bg-[#1a1a1a] gap-4">
        <AlertCircle className="w-12 h-12 text-gray-500 dark:text-slate-400" />
        <p className="text-gray-300 text-lg">Sin condominio asignado</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-full bg-[#1a1a1a] gap-4 p-6">
        <AlertCircle className="w-12 h-12 text-red-400" />
        <p className="text-gray-300">{loadError}</p>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-[#1a1a1a] p-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[var(--v-text-primary)] mb-2 flex items-center gap-3">
            <Building2 className="w-7 h-7 text-[#36CCD8]" /> Panel del Condominio
          </h1>
          <p className="text-gray-100">
            Identidad del condominio y activación de módulos. Los módulos están limitados por tu plan
            {planName ? <> (plan actual: <strong className="text-[#36CCD8]">{planName}</strong>)</> : null}.
          </p>
          {!entitlementsAvailable && (
            <div className="mt-3 p-3 rounded-lg bg-yellow-500/20 border border-yellow-500/50 text-sm text-yellow-100">
              No se pudo leer el plan efectivo (/entitlements/:condoId). Los bloqueos por plan están
              desactivados temporalmente; el servidor seguirá re-validando cada cambio.
            </div>
          )}
        </div>

        {/* Condo identity */}
        <Card className="bg-[#2a2a2a] border-[#3a3a3a] shadow-lg mb-6">
          <CardHeader>
            <CardTitle className="text-xl text-white font-bold">Identidad del Condominio</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-200 mb-1">Nombre</label>
                <input value={name} onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-[#4a4a4a] bg-[#1a1a1a] text-white" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-200 mb-1">Ciudad</label>
                <input value={city} onChange={(e) => setCity(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-[#4a4a4a] bg-[#1a1a1a] text-white" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <label className="block text-sm font-semibold text-gray-200 mb-1">Calle</label>
                  <input value={street} onChange={(e) => setStreet(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-[#4a4a4a] bg-[#1a1a1a] text-white" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-200 mb-1">Número</label>
                  <input value={number} onChange={(e) => setNumber(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-[#4a4a4a] bg-[#1a1a1a] text-white" />
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-200 mb-1">Descripción</label>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
                  className="w-full px-3 py-2 rounded-lg border border-[#4a4a4a] bg-[#1a1a1a] text-white resize-none" />
                <p className="mt-1 text-xs text-gray-400">
                  Nota: el backend aún no persiste `description` en PATCH /condos/:id (pendiente de
                  allowlist); se envía y se guardará en cuanto esté disponible.
                </p>
              </div>
              {/* Logo upload (2026-09-08): live backend POST /api/condos/:id/logo
                  (condo/routes.js) accepts JSON {file: "<base64 or data-url>"} and
                  returns { success, data: { logoUrl } }. Client validates 2MB + image type. */}
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-200 mb-1 flex items-center gap-2">
                  <ImageIcon className="w-4 h-4" /> Logotipo
                </label>
                <div className="flex items-center gap-3 p-3 rounded-lg border border-dashed border-[#4a4a4a]">
                  <div className="w-12 h-12 rounded-lg bg-[#1a1a1a] flex items-center justify-center overflow-hidden">
                    {logoUrl ? <img src={logoUrl} alt="Logo" className="w-full h-full object-cover" /> : <Building2 className="w-6 h-6 text-gray-500" />}
                  </div>
                  <div className="flex-1">
                    <label className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#1a1a1a] border border-[#4a4a4a] text-sm cursor-pointer hover:border-[#36CCD8] transition-colors ${logoBusy ? 'opacity-60 pointer-events-none' : ''}`}>
                      <Upload className="w-4 h-4" /> {logoBusy ? 'Subiendo…' : 'Subir logo'}
                      <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" className="hidden" disabled={logoBusy} onChange={handleLogoFile} />
                    </label>
                    <p className="text-xs text-gray-500 mt-1">JPEG, PNG, GIF o WebP. Máximo 2MB.</p>
                    {logoError && <p className="text-xs text-red-400 mt-1" role="alert">{logoError}</p>}
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-5 flex justify-end items-center gap-3">
              {identityMsg && <span className="text-sm text-emerald-400">{identityMsg}</span>}
              <Button onClick={handleSaveIdentity} disabled={savingIdentity}
                className="bg-[#36CCD8] hover:bg-[#2DB8C4] text-black font-semibold">
                {savingIdentity ? <><Save className="w-4 h-4 mr-2" /> Guardando…</> : 'Guardar identidad'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Module toggles (plan-bounded) */}
        <div className="grid gap-6">
          {Object.entries(MODULE_CATEGORIES).map(([key, category]) => {
            const Icon = category.icon;
            return (
              <Card key={key} className="bg-[#2a2a2a] border-[#3a3a3a] shadow-lg">
                <CardHeader className="flex flex-row items-center gap-4">
                  <div className={`p-3 rounded-lg ${category.color}`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <CardTitle className="text-xl text-white font-bold">{category.name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {category.modules.map(module => {
                      const locked = isModuleLocked(module.id);
                      return (
                        <div key={module.id}
                          className={'flex items-start justify-between p-4 rounded-lg bg-[#1a1a1a] border transition-colors ' + (locked ? 'border-[#3a3a3a] opacity-70' : 'border-[#4a4a4a] hover:border-[#36CCD8]')}>
                          <div className="flex-1 mr-4">
                            <h3 className="font-bold text-white mb-1 flex items-center gap-2">
                              {module.name}
                              {locked && <Lock className="w-3.5 h-3.5 text-yellow-400" />}
                            </h3>
                            <p className="text-sm text-gray-100">{module.description}</p>
                            {moduleSources[module.id] === 'override' || moduleSources[module.id] === 'legacy_config' ? (
                              <button
                                type="button"
                                aria-label={t('condoAdmin.resetToDefault')}
                                title={t('condoAdmin.resetToDefault')}
                                disabled={resettingModule === module.id}
                                onClick={() => handleResetModule(module.id)}
                                className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-gray-300 hover:text-[#36CCD8] disabled:opacity-50"
                              >
                                <RotateCcw className={resettingModule === module.id ? 'w-3 h-3 animate-spin' : 'w-3 h-3'} />
                                {t('condoAdmin.resetToDefault')}
                              </button>
                            ) : null}
                            {locked && (
                              <Link to="/pricing" className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#36CCD8] hover:underline">
                                <Sparkles className="w-3 h-3" /> Mejorar plan para desbloquear
                              </Link>
                            )}
                          </div>
                          <Switch
                            checked={!locked && (moduleConfig[module.id] ?? true)}
                            onCheckedChange={() => handleToggle(module.id)}
                            disabled={locked}
                            className="data-[state=checked]:bg-[#36CCD8]"
                          />
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="mt-8 flex justify-end">
          <Button onClick={handleSaveModules} disabled={savingModules}
            className="bg-[#36CCD8] hover:bg-[#2DB8C4] text-black font-semibold px-8">
            {savingModules ? 'Guardando…' : 'Guardar Cambios'}
          </Button>
        </div>

        <div className="mt-6 p-4 rounded-lg bg-yellow-500/20 border border-yellow-500/50">
          <h4 className="text-yellow-200 font-bold mb-2">⚠️ Nota importante</h4>
          <p className="text-sm text-gray-100">
            Los módulos desactivados no aparecerán en el menú lateral ni serán accesibles por ningún
            usuario del condominio. Los módulos bloqueados por tu plan no se pueden activar aquí;
            el backend re-valida cada activación contra el resolver de entitlements.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CondoAdminPanel;
